import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'

import { IPermitTypeField, PermitFieldType } from '~/client/graph'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import {
  VALID_EMAIL_PATTERN,
  VALID_NUMBER_PATTERN,
} from '~/client/src/shared/utils/regExpPatterns'

import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'
import PermitFieldRemoveIcon from './PermitFieldRemoveIcon'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  tableId?: string
  tableRowIndex?: number
  parentInstanceId?: string
  parentValueIndex?: number
}

const EMAIL_TYPE = 'email'
const NUMBER_TYPE = 'number'
const TEXT_TYPE = 'text'
const TIME_TYPE = 'time'

@observer
export default class PermitFormInputField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const {
      isViewMode,
      typeField,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
      store,
    } = this.props

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={this.renderComponent}
        onAddFieldClick={this.addNewFieldValue}
        onRemoveField={this.removeValue}
      />
    )
  }

  private renderComponent = (
    value: string,
    index: number,
    isHeaderShown?: boolean,
  ): JSX.Element => {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    const { caption, isMandatory, isMultiple } = typeField
    const { isFieldChanged, isFieldValid } = fieldsStore

    const shouldShowRemoveBtn = !!index || !!value

    return (
      <div key={index} className="row y-center">
        <StruxhubInput
          className="overflow-hidden"
          label={caption}
          type={this.inputType}
          negativeOrDecimal={this.isNumberType}
          isAlternativeNumberMode={this.isNumberType}
          autoComplete={this.autoCompleteType}
          isRequired={isMandatory}
          isChanged={isFieldChanged(
            typeField.id,
            index,
            tableId,
            tableRowIndex,
            parentInstanceId,
            parentValueIndex,
          )}
          isValid={isFieldValid(
            typeField,
            index,
            tableId,
            tableRowIndex,
            parentInstanceId,
            parentValueIndex,
          )}
          isLabelHidden={!!index && !isHeaderShown}
          isHelperTextCondensed={isMultiple && !isHeaderShown}
          isTopMarginForced={isMultiple && !index}
          value={value || ''}
          onChange={this.onValueChange.bind(this, index)}
          onValueReset={
            !isMultiple &&
            !this.isTableField &&
            this.removeValue.bind(this, index)
          }
          validationMessage={this.validationMessage}
        />
        {isMultiple && shouldShowRemoveBtn && !isHeaderShown && (
          <PermitFieldRemoveIcon index={index} onClick={this.removeValue} />
        )}
      </div>
    )
  }

  private get fieldValues(): string[] {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    const values = fieldsStore.getFieldValues<string>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : ['']
  }

  private get isEmailType(): boolean {
    return this.localProps.typeField.type === PermitFieldType.Email
  }

  private get isNumberType(): boolean {
    return this.localProps.typeField.type === PermitFieldType.InputNumber
  }

  private get isTimeType(): boolean {
    return this.localProps.typeField.type === PermitFieldType.Time
  }

  private get inputType(): string {
    if (this.isEmailType) {
      return EMAIL_TYPE
    }
    if (this.isNumberType) {
      return NUMBER_TYPE
    }
    if (this.isTimeType) {
      return TIME_TYPE
    }
    return TEXT_TYPE
  }

  private get autoCompleteType(): string {
    if (this.isNumberType) {
      return 'off'
    }
  }

  private get validationMessage(): string {
    if (this.isEmailType) {
      return Localization.translator.invalidEmailError
    }
    if (this.isNumberType) {
      return Localization.translator.invalidNumberFormat
    }
  }

  private isEmailValid(value: string): boolean {
    return !value || VALID_EMAIL_PATTERN.test(value.toLowerCase())
  }

  private isNumberValid(value: string): boolean {
    return !value || VALID_NUMBER_PATTERN.test(value)
  }

  private onValueChange(
    fieldIndex: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const {
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props
    const { changeValidationState, changeFieldValue, changeTableFieldValue } =
      fieldsStore
    const { value } = event.target

    if (this.isEmailType) {
      changeValidationState(
        typeField.id,
        fieldIndex,
        this.isEmailValid(value),
        tableRowIndex,
        parentInstanceId,
        parentValueIndex,
      )
    }
    if (this.isNumberType) {
      changeValidationState(
        typeField.id,
        fieldIndex,
        this.isNumberValid(value),
        tableRowIndex,
      )
    }
    if (this.isTableField) {
      changeTableFieldValue(
        tableId,
        tableRowIndex,
        typeField,
        fieldIndex,
        value,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }

    changeFieldValue(
      typeField,
      fieldIndex,
      value,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private addNewFieldValue = () => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.addNewFieldValue(
        typeField,
        '',
        parentInstanceId,
        parentValueIndex,
      )
    }
  }

  private removeValue = (index: number) => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props
      const { rearrangeInvalidState, removeFieldValue } = fieldsStore

      rearrangeInvalidState(
        typeField.id,
        index,
        parentInstanceId,
        parentValueIndex,
      )
      removeFieldValue(typeField.id, index, parentInstanceId, parentValueIndex)
    }
  }

  private get isTableField(): boolean {
    return !!this.localProps.tableId
  }
}
